/* eslint-disable react/prop-types */
/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx, keyframes } from '@emotion/core';
import { forwardRef, memo, useImperativeHandle, useLayoutEffect, useState } from 'react';
import { bgColor, borderRadius, inset, overflowHidden, positionAbsolute } from "../../styles/general";
const useDebouncedRippleCleanUp = (rippleCount, duration, cleanUpFunction) => {
  useLayoutEffect(() => {
    if (rippleCount > 0) {
      const bounce = setTimeout(() => cleanUpFunction(), duration);
      return () => clearTimeout(bounce);
    }
    return undefined;
  }, [rippleCount, duration, cleanUpFunction]);
};
const Ripple = /*#__PURE__*/memo( /*#__PURE__*/forwardRef(({
  duration = 850,
  color = 'system.white',
  circular = false,
  ...props
}, ref) => {
  const [rippleArray, setRippleArray] = useState([]);
  useDebouncedRippleCleanUp(rippleArray.length, duration, () => {
    setRippleArray([]);
  });
  const _RippleChildCSS = RippleChildCSS(circular, color, duration);
  const addRipple = event => {
    const rippleContainer = event.currentTarget.getBoundingClientRect();
    const size = rippleContainer.width > rippleContainer.height ? rippleContainer.width : rippleContainer.height;
    const x = event.pageX - rippleContainer.x - rippleContainer.width / 2;
    const y = event.pageY - rippleContainer.y - rippleContainer.width / 2;
    const newRippleArray = {
      x,
      y,
      size
    };
    setRippleArray(prevState => [...prevState, newRippleArray]);
  };
  useImperativeHandle(ref, () => ({
    start: addRipple
  }));
  return jsx("span", {
    css: RippleContainerCSS,
    className: 'DGN-UI-Ripple',
    ...props
  }, rippleArray.length > 0 && rippleArray.map((ripple, index) => {
    return jsx("span", {
      key: 'ripple_' + index,
      css: _RippleChildCSS,
      className: 'DGN-UI-Ripple-Child',
      style: {
        top: ripple.y,
        left: ripple.x,
        width: ripple.size,
        height: ripple.size
      }
    });
  }));
}));
const rippleKeyframes = keyframes`
	to {
		opacity: 0;
		transform: scale(2);
	}
`;
const RippleContainerCSS = css`
    ${overflowHidden};
    ${positionAbsolute};
    z-index: 0;
    ${inset(0)};
    border-radius: inherit;
`;
const RippleChildCSS = (circular, color, duration) => css`
    ${positionAbsolute};
    ${borderRadius(circular ? '50%' : '100%')};
    opacity: 0.75;
    transform: scale(0);
    ${bgColor(color)};
    animation: ${rippleKeyframes} ${duration}ms;
`;
export default Ripple;